import { LARK_CONFIG } from './config.js';

/**
 * Lark BASE API Client (Worker Proxy Version)
 * Uses Cloudflare Pages Functions as proxy to avoid CORS issues
 */
export class LarkAPIClient {
  constructor() {
    // Determine the API base URL based on environment
    this.proxyUrl = this.getProxyUrl();
  }

  /**
   * Get the proxy URL based on environment
   */
  getProxyUrl() {
    // In production (Cloudflare Pages), use relative URL
    if (window.location.hostname.includes('pages.dev') || 
        window.location.hostname.includes('cloudflare')) {
      return '/api/lark-proxy';
    }
    
    // For local development, use full URL to deployed function
    return 'https://lark-cashflow-plugin.pages.dev/api/lark-proxy';
  }

  /**
   * Get records from BASE table via Worker proxy
   * @param {string} tableId - Table ID
   * @param {Object} options - Query options
   */
  async getRecords(tableId, options = {}) {
    const { pageSize = 500, pageToken } = options;

    try {
      const response = await fetch(this.proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'getRecords',
          params: {
            pageSize,
            pageToken
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API request failed: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        return {
          records: data.records || [],
          hasMore: data.hasMore || false,
          pageToken: data.pageToken || null,
          total: data.total || 0
        };
      } else {
        throw new Error(`Failed to get records: ${data.message || data.error}`);
      }
    } catch (error) {
      console.error('Error getting records:', error);
      throw error;
    }
  }

  /**
   * Get all records (handle pagination automatically)
   * @param {string} tableId - Table ID
   * @param {Object} options - Query options
   */
  async getAllRecords(tableId, options = {}) {
    let allRecords = [];
    let hasMore = true;
    let pageToken = null;

    while (hasMore) {
      const result = await this.getRecords(tableId, {
        ...options,
        pageToken
      });

      allRecords = allRecords.concat(result.records);
      hasMore = result.hasMore;
      pageToken = result.pageToken;
    }

    return allRecords;
  }

  /**
   * Get transaction records with date range filter
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   */
  async getTransactionsByDateRange(startDate, endDate) {
    const allRecords = await this.getAllRecords(LARK_CONFIG.TRANSACTIONS_TABLE_ID);
    
    // Filter by date range (client-side filtering)
    const filtered = allRecords.filter(record => {
      const transactionDate = new Date(record.fields['取引日']);
      return transactionDate >= startDate && transactionDate <= endDate;
    });

    return filtered;
  }

  /**
   * Parse record to transaction object
   * @param {Object} record - Raw record from API
   */
  parseTransaction(record) {
    const fields = record.fields || {};
    
    // Helper function to safely parse numeric values
    const parseNumber = (value) => {
      if (value === null || value === undefined || value === '') return 0;
      const num = parseFloat(value);
      return isNaN(num) ? 0 : num;
    };
    
    // Helper function to safely parse date values
    const parseDate = (value) => {
      if (!value) return null;
      // Lark returns timestamp in milliseconds
      return new Date(value);
    };
    
    return {
      id: record.record_id,
      key: fields['主キー'] || fields['キー'] || '',
      transaction_date: parseDate(fields['取引日']),
      debit_account: fields['借方勘定科目'] || '',
      debit_partner: fields['借方取引先名'] || '',
      debit_amount: parseNumber(fields['借方金額']),
      debit_note: fields['借方備考'] || '',
      debit_tax_type: fields['借方税区分'] || '',
      debit_tax_amount: parseNumber(fields['借方税金額']),
      debit_tax_classification: fields['借方内税・外税'] || '',
      credit_account: fields['貸方勘定科目'] || '',
      credit_amount: parseNumber(fields['貸方金額']),
      credit_tax_amount: parseNumber(fields['貸方税金額']),
      credit_tax_classification: fields['貸方内税・外税'] || '',
      credit_item: fields['貸方品目'] || '',
      created_at: parseDate(fields['作成日時'])
    };
  }
}

// Export singleton instance
export const larkAPI = new LarkAPIClient();
